import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useUpdateDoctor } from "@/services/query/doctors.query"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { toast } from "sonner"
import PropTypes from "prop-types"
import { Loader2, Plus, X, HelpCircle } from "lucide-react"
import { useEffect } from "react"
import ReactMarkdown from "react-markdown"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"

const formSchema = z.object({
  descriptions: z.array(
    z.object({
      key: z.string().min(1, "Section title is required"),
      value: z.string().min(10, "Content must be at least 10 characters"),
    })
  ),
})

const sectionTemplates = {
  experience: {
    key: "Experience",
    value:
      "- Senior Consultant at City Hospital (2018-Present)\n- Specialist at Medical Center (2015-2018)\n- Resident Doctor at General Hospital (2012-2015)",
  },
  expertise: {
    key: "Areas of Expertise",
    value:
      "- **Specialty 1**: Detailed description\n- **Specialty 2**: Detailed description\n- **Specialty 3**: Detailed description",
  },
  education: {
    key: "Education & Training",
    value:
      "- **Medical Degree**: University Name, Year\n- **Specialization**: Field, Institution, Year\n- **Additional Certifications**: Details",
  },
  symptoms: {
    key: "Common Symptoms",
    value:
      "Neurologists commonly treat a variety of symptoms and conditions, including but not limited to:\n\n- **Headaches**: Chronic migraines, tension headaches, and cluster headaches.\n- **Seizures**: Epileptic seizures and other seizure disorders.\n- **Dizziness and Balance Disorders**: Vertigo and issues related to balance.\n- **Nerve Pain**: Conditions such as neuropathy and radiculopathy.\n- **Cognitive Impairments**: Memory loss, confusion, and other cognitive dysfunctions.\n- **Movement Disorders**: Parkinson's disease, essential tremors, and dystonia.\n- **Multiple Sclerosis**: A chronic disease affecting the central nervous system.\n- **Stroke**: Acute management and rehabilitation following a stroke.\n- **Sleep Disorders**: Conditions like sleep apnea and restless leg syndrome.",
  },
}

export function EditDoctorDescriptionDialog({ doctor, open, onOpenChange }) {
  const updateDoctorMutation = useUpdateDoctor()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      descriptions: [{ key: "", value: "" }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "descriptions",
  })

  useEffect(() => {
    if (doctor && open) {
      const descriptions = doctor.details
        ?.split(/(?=### )/g) // Split on section headers while keeping the delimiter
        .filter((section) => section.trim()) // Remove empty sections
        .map((section) => {
          const lines = section.split("\n")
          const title = lines[0].replace("### ", "").trim()
          const content = lines.slice(1).join("\n").trim()
          return {
            key: title,
            value: content,
          }
        })

      form.reset({
        descriptions:
          descriptions?.length > 0 ? descriptions : [{ key: "", value: "" }],
      })
    }
  }, [doctor, open, form])

  const onSubmit = async (values) => {
    try {
      const formattedDetails = values.descriptions
        .map(({ key, value }) => `### ${key}\n${value}`)
        .join("\n\n")

      await updateDoctorMutation.mutateAsync({
        id: doctor.id,
        data: {
          details: formattedDetails,
        },
      })

      toast.success("Doctor description updated successfully")
      onOpenChange(false)
    } catch (error) {
      toast.error(error.message || "Failed to update doctor description")
    }
  }

  const handleTemplateSelect = (template, index) => {
    const templateContent = sectionTemplates[template]
    form.setValue(`descriptions.${index}.key`, templateContent.key)
    form.setValue(`descriptions.${index}.value`, templateContent.value)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[1200px] h-[95vh] sm:h-[90vh] flex flex-col p-4 sm:p-6">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            Edit Doctor Description
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
            Modify the doctor's description sections below.
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="inline-flex">
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] sm:max-w-sm p-3">
                  <p className="font-medium mb-2">Markdown Syntax Guide:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>**bold text** for bold</li>
                    <li>- for bullet points</li>
                    <li>### for section headers</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 flex-1 overflow-hidden">
              {/* Editor Section */}
              <div className="flex flex-col overflow-hidden order-2 lg:order-1">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                  <FormLabel className="text-base sm:text-lg font-semibold">
                    Description Sections
                  </FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ key: "", value: "" })}
                    className="w-full sm:w-auto flex items-center gap-2 border-blue-500 text-blue-500 hover:bg-blue-50"
                  >
                    <Plus className="h-4 w-4" /> Add Section
                  </Button>
                </div>

                <ScrollArea className="flex-1 px-1 -mx-1">
                  <div className="space-y-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="p-3 sm:p-4 rounded-lg border border-gray-200 bg-white shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3 sm:mb-4">
                          <FormField
                            control={form.control}
                            name={`descriptions.${index}.key`}
                            render={({ field }) => (
                              <FormItem className="flex-1">
                                <FormLabel className="text-sm font-medium">
                                  Section Title
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    className="focus-visible:ring-blue-500"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Select
                            onValueChange={(value) =>
                              handleTemplateSelect(value, index)
                            }
                          >
                            <SelectTrigger className="w-full sm:w-[180px] mt-[22px]">
                              <SelectValue placeholder="Templates" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(sectionTemplates).map(
                                ([key, template]) => (
                                  <SelectItem key={key} value={key}>
                                    {template.key}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <FormField
                          control={form.control}
                          name={`descriptions.${index}.value`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Content
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  className="min-h-[150px] sm:min-h-[200px] font-mono text-sm focus-visible:ring-blue-500"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="mt-2 w-full sm:w-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4 mr-2" /> Remove Section
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Preview Section */}
              <div className="flex flex-col overflow-hidden order-1 lg:order-2 h-[300px] lg:h-auto">
                <div className="text-base sm:text-lg font-semibold mb-2 sm:mb-4 flex items-center gap-2">
                  Preview
                  <Badge variant="secondary" className="font-normal">
                    Live
                  </Badge>
                </div>
                <div className="border rounded-lg p-3 sm:p-6 bg-white overflow-hidden flex flex-col flex-1">
                  <ScrollArea className="flex-1 pr-4">
                    <div className="prose prose-sm sm:prose prose-blue max-w-none">
                      <ReactMarkdown>
                        {form
                          .watch("descriptions")
                          .map((desc) => `### ${desc.key}\n${desc.value}`)
                          .join("\n\n")}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 gap-2 pt-4 mt-4 sm:mt-6 border-t">
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={updateDoctorMutation.isPending}
                  className="w-full sm:w-auto order-2 sm:order-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateDoctorMutation.isPending}
                  className="w-full sm:w-auto min-w-[120px] order-1 sm:order-2"
                >
                  {updateDoctorMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </div>
                  ) : (
                    "Update Description"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

EditDoctorDescriptionDialog.propTypes = {
  doctor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    details: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}
